/**
 * My AI Agents — build, test, publish and monetise AI advisors on the iMaven marketplace.
 *
 * One expert = one agent (2026-10-19): the agent created here is the marketplace storefront of the
 * expert's own brain, which they edit in Synthora as themselves. Lifecycle (server state machine
 * in agentService):
 *   draft → testing (Test Bench must pass) → pending_review (CRM) → published ⇄ paused.
 *
 * API: GET /agents/me · POST /agents · GET/PUT/DELETE /agents/:id · /agents/:id/programs ·
 *      /agents/:id/bench/run|runs|runs/:rid · /agents/:id/submit|pause|resume · /agents/:id/earnings ·
 *      PUT /agents/:id/responsibility · PUT /agents/:id/credential · POST|GET /agents/:id/credential/document
 *
 * Every agent needs the owner's responsibility confirmation before submit; in regulated categories
 * (agent.credential.required) also a licence + document + attestation, and it only publishes once
 * iMaven verifies the licence in the CRM.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ReviewFindings from '../Components/TestBench/ReviewFindings';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import { useToast } from '../Context/ToastContext';

// ============================================
// STYLES (dashboard dark palette)
// ============================================
const Page = styled.div` height:100%; overflow-y:auto; background:#0f1419; color:#e6edf3; padding:28px 32px; `;
const Title = styled.h1` font-size:24px; margin:0; font-weight:600; `;
const Subtitle = styled.p` color:#8b98a5; margin:4px 0 24px; font-size:14px; max-width:760px; line-height:1.5; `;
const Grid = styled.div` display:grid; grid-template-columns:280px minmax(0,1fr); gap:20px; @media (max-width:1000px){ grid-template-columns:1fr; } `;
const Card = styled.div` background:#161b22; border:1px solid rgba(255,255,255,0.08); border-radius:12px; padding:18px 20px; margin-bottom:16px; `;
const CardTitle = styled.h3` margin:0 0 12px; font-size:15px; font-weight:600; color:#e6edf3; display:flex; align-items:center; justify-content:space-between; gap:10px; `;
const Label = styled.label` display:block; font-size:12px; font-weight:600; color:#8b98a5; margin:12px 0 6px; text-transform:uppercase; letter-spacing:.06em; `;
const Input = styled.input` width:100%; background:#0f1419; border:1px solid rgba(255,255,255,0.12); color:#e6edf3; border-radius:8px; padding:10px 12px; font-size:14px; outline:none; &:focus{border-color:#2f81f7;} `;
const TextArea = styled.textarea` width:100%; min-height:90px; background:#0f1419; border:1px solid rgba(255,255,255,0.12); color:#e6edf3; border-radius:8px; padding:10px 12px; font-size:14px; line-height:1.5; outline:none; resize:vertical; &:focus{border-color:#2f81f7;} `;
const Select = styled.select` background:#0f1419; border:1px solid rgba(255,255,255,0.12); color:#e6edf3; border-radius:8px; padding:10px 12px; font-size:14px; max-width:100%; `;
const PrimaryBtn = styled.button` background:#2f81f7; color:#fff; border:none; border-radius:8px; padding:10px 18px; font-size:14px; font-weight:600; cursor:pointer; &:hover{background:#4f9bff;} &:disabled{opacity:.5;cursor:default;} `;
const GhostBtn = styled.button` background:transparent; color:#e6edf3; border:1px solid rgba(255,255,255,0.14); border-radius:8px; padding:10px 18px; font-size:14px; font-weight:600; cursor:pointer; &:hover{background:rgba(255,255,255,0.06);} &:disabled{opacity:.5;cursor:default;} `;
const DangerBtn = styled(GhostBtn)` color:#f85149; border-color:rgba(248,81,73,0.4); `;
const Row = styled.div` display:flex; gap:10px; flex-wrap:wrap; align-items:center; `;
const Help = styled.div` color:#8b98a5; font-size:12px; margin-top:6px; line-height:1.5; `;
const Pill = styled.span` display:inline-block; padding:3px 10px; border-radius:999px; font-size:12px; font-weight:600; background:${p=>p.$bg||'rgba(255,255,255,0.08)'}; color:${p=>p.$fg||'#e6edf3'}; `;
const Chip = styled.button` border:1px solid ${p=>p.$on?'#2f81f7':'rgba(255,255,255,0.14)'}; background:${p=>p.$on?'rgba(47,129,247,0.18)':'transparent'}; color:#e6edf3; border-radius:999px; padding:5px 12px; font-size:12px; cursor:pointer; `;
const AgentItem = styled.button` width:100%; text-align:left; background:${p=>p.$active?'rgba(47,129,247,0.15)':'#161b22'}; border:1px solid ${p=>p.$active?'rgba(47,129,247,0.5)':'rgba(255,255,255,0.08)'}; border-radius:10px; padding:12px 14px; margin-bottom:8px; color:#e6edf3; cursor:pointer; &:hover{border-color:rgba(47,129,247,0.5);} `;
const Steps = styled.ol` list-style:none; padding:0; margin:0; display:grid; gap:8px; `;
const Step = styled.li` display:flex; gap:10px; align-items:flex-start; font-size:13px; color:${p=>p.$done?'#3fb950':p.$active?'#e6edf3':'#8b98a5'}; `;
const Dot = styled.span` width:18px; height:18px; border-radius:50%; flex-shrink:0; display:inline-flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; background:${p=>p.$done?'#3fb950':p.$active?'#2f81f7':'rgba(255,255,255,0.1)'}; color:#fff; `;
const Stat = styled.div` background:#0f1419; border:1px solid rgba(255,255,255,0.08); border-radius:10px; padding:12px 14px; `;
const StatV = styled.div` font-size:22px; font-weight:700; `;
const StatL = styled.div` font-size:12px; color:#8b98a5; `;
const RunRow = styled.div` display:flex; justify-content:space-between; gap:10px; padding:10px 0; border-top:1px solid rgba(255,255,255,0.06); font-size:13px; cursor:pointer; &:hover{background:rgba(255,255,255,0.03);} `;
const Transcript = styled.div` max-height:360px; overflow-y:auto; background:#0f1419; border:1px solid rgba(255,255,255,0.08); border-radius:10px; padding:12px; font-size:13px; line-height:1.5; `;
const Turn = styled.div` margin-bottom:10px; b{color:#8b98a5;} `;
const SampleBox = styled.div` border:1px solid rgba(255,255,255,0.08); border-radius:10px; padding:12px; margin-top:10px; background:#0f1419; `;

const STATUS_TEXT = {
  draft: ['Draft', 'rgba(139,152,165,0.2)', '#c9d1d9'],
  testing: ['Testing', 'rgba(47,129,247,0.2)', '#79c0ff'],
  pending_review: ['Pending review', 'rgba(210,153,34,0.2)', '#e3b341'],
  published: ['Live', 'rgba(63,185,80,0.2)', '#56d364'],
  paused: ['Paused', 'rgba(139,152,165,0.2)', '#c9d1d9'],
  rejected: ['Not approved', 'rgba(248,81,73,0.2)', '#ff7b72'],
};
const BLOCKING_TEXT = {
  'no-headline': 'Add a headline.',
  'no-description': 'Add a description.',
  'no-passing-test': 'Run the test until it passes (valid 30 days).',
  'not-approved': 'Waiting for iMaven review.',
  'responsibility-required': 'Confirm that you are responsible for the agent.',
  'responsibility-outdated': 'The responsibility statement changed. Read it and confirm again.',
  'licence-required': 'Register your professional licence, with the document.',
  'licence-submitted': 'Waiting for iMaven to verify your licence.',
  'licence-rejected': 'Your licence was not verified. Fix it and save again.',
};
const LICENCE_STATUS = {
  submitted: ['Waiting for iMaven to verify', 'rgba(210,153,34,0.2)', '#e3b341'],
  verified: ['Verified', 'rgba(63,185,80,0.2)', '#56d364'],
  rejected: ['Not verified', 'rgba(248,81,73,0.2)', '#ff7b72'],
};
const LICENCE_COUNTRIES = [['CA', 'Canada'], ['US', 'United States'], ['IL', 'Israel']];
const LICENCE_DOC_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const LICENCE_DOC_MAX = 10 * 1024 * 1024;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');
const errText = (e, fallback) => e?.response?.data?.error || fallback;
const TextBox = styled.div` background:#0f1419; border:1px solid rgba(255,255,255,0.08); border-radius:10px; padding:12px 14px; font-size:13px; line-height:1.6; color:#c9d1d9; white-space:pre-wrap; `;
const Check = styled.label` display:flex; gap:10px; align-items:flex-start; font-size:13px; color:#e6edf3; margin-top:12px; cursor:pointer; input{margin-top:3px; flex-shrink:0;} `;
const FormGrid = styled.div` display:grid; grid-template-columns:1fr 1fr; gap:0 14px; @media (max-width:700px){ grid-template-columns:1fr; } `;

const credentialToForm = (c) => ({
  holderName: c?.holderName || '',
  profession: c?.profession || '',
  regulator: c?.regulator || '',
  licenseNumber: c?.licenseNumber || '',
  jurisdictions: c?.jurisdictions || [],
  expiresAt: c?.expiresAt ? String(c.expiresAt).slice(0, 10) : '',
  attestationAccepted: false,
});

/**
 * "Your responsibility" (every agent) + "Professional licence" (required in regulated categories,
 * optional elsewhere). Server: PUT /agents/:id/responsibility · PUT /agents/:id/credential ·
 * POST|GET /agents/:id/credential/document. Every call returns the fresh agent view.
 */
function AgentCredentialCards({ agent, api, showToast, replaceAgent }) {
  const cv = agent.credential || {};
  const lic = cv.credential || null;
  const required = !!cv.required;
  const [respChecked, setRespChecked] = useState(false);
  const [respSaving, setRespSaving] = useState(false);
  const [open, setOpen] = useState(required);
  const [lf, setLf] = useState(credentialToForm(lic));
  const [isoInput, setIsoInput] = useState('');
  const [licSaving, setLicSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Reset when another agent is selected.
  useEffect(() => {
    setRespChecked(false);
    setOpen(!!agent.credential?.required || agent.credential?.credential?.status === 'rejected');
    setLf(credentialToForm(agent.credential?.credential));
    setIsoInput('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.id]);

  const setL = (patch) => setLf((f) => ({ ...f, ...patch }));
  const toggleCountry = (code) => setL({ jurisdictions: lf.jurisdictions.includes(code) ? lf.jurisdictions.filter((x) => x !== code) : [...lf.jurisdictions, code].slice(0, 10) });
  const addIso = () => {
    const code = isoInput.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) { showToast('Enter a two-letter country code, e.g. GB', 'error'); return; }
    if (!lf.jurisdictions.includes(code)) setL({ jurisdictions: [...lf.jurisdictions, code].slice(0, 10) });
    setIsoInput('');
  };

  const reviewWarning = required
    ? 'Your licence is verified. Changing it sends it back to iMaven for review, and the agent is hidden from the marketplace until it is verified again. Continue?'
    : 'Your licence is verified. Changing it sends it back to iMaven for review, and the "Licensed professional" badge is removed until it is verified again. Continue?';

  const confirmResponsibility = async () => {
    setRespSaving(true);
    try {
      const r = await api.put(`/agents/${agent.id}/responsibility`, { accepted: true });
      if (r.data?.agent) replaceAgent(r.data.agent);
      setRespChecked(false);
      showToast('Thank you. Your confirmation is recorded.', 'success');
    } catch (e) { showToast(errText(e, 'Could not record your confirmation'), 'error'); }
    finally { setRespSaving(false); }
  };

  const saveLicence = async () => {
    if (lic?.status === 'verified' && !window.confirm(reviewWarning)) return;
    setLicSaving(true);
    try {
      const r = await api.put(`/agents/${agent.id}/credential`, {
        holderName: lf.holderName, profession: lf.profession, regulator: lf.regulator, licenseNumber: lf.licenseNumber,
        jurisdictions: lf.jurisdictions, expiresAt: lf.expiresAt || undefined, attestationAccepted: lf.attestationAccepted,
      });
      if (r.data?.agent) { replaceAgent(r.data.agent); setLf(credentialToForm(r.data.agent.credential?.credential)); }
      showToast(lic?.hasDocument ? 'Licence saved. iMaven will verify it.' : 'Licence saved. Now upload the licence document.', 'success');
    } catch (e) { showToast(errText(e, 'Could not save the licence'), 'error'); }
    finally { setLicSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!LICENCE_DOC_TYPES.includes(file.type)) { showToast('Upload the licence as a PDF, JPG or PNG file.', 'error'); return; }
    if (file.size > LICENCE_DOC_MAX) { showToast('The file is larger than 10 MB.', 'error'); return; }
    if (lic?.status === 'verified' && !window.confirm(reviewWarning)) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('document', file);
      const r = await api.post(`/agents/${agent.id}/credential/document`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data?.agent) replaceAgent(r.data.agent);
      showToast('Document uploaded. iMaven will verify your licence.', 'success');
    } catch (e) { showToast(errText(e, 'Could not upload the document'), 'error'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const viewDocument = async () => {
    // Open the tab synchronously so the popup blocker allows it, then point it at the signed link.
    const win = window.open('', '_blank');
    try {
      const r = await api.get(`/agents/${agent.id}/credential/document`);
      if (win) { win.opener = null; win.location.href = r.data.url; } else window.open(r.data.url, '_blank', 'noopener');
    } catch (e) { if (win) win.close(); showToast(errText(e, 'Could not open the document'), 'error'); }
  };

  const resp = cv.responsibility;
  const respOk = !!resp && resp.current;
  const [lsText, lsBg, lsFg] = (lic && LICENCE_STATUS[lic.status]) || [];
  const canSave = !licSaving && lf.holderName.trim() && lf.profession.trim() && lf.regulator.trim() && lf.licenseNumber.trim() && lf.jurisdictions.length > 0 && lf.attestationAccepted;
  const today = new Date().toISOString().slice(0, 10);
  const extraCodes = lf.jurisdictions.filter((j) => !LICENCE_COUNTRIES.some(([c]) => c === j));

  return (
    <>
      <Card style={!respOk ? { borderColor: 'rgba(210,153,34,0.5)' } : undefined}>
        <CardTitle>
          <span>Your responsibility</span>
          {respOk ? <Pill $bg="rgba(63,185,80,0.2)" $fg="#56d364">Confirmed</Pill> : <Pill $bg="rgba(210,153,34,0.2)" $fg="#e3b341">Needed to submit</Pill>}
        </CardTitle>
        {resp && !resp.current && <Help style={{ marginTop: 0, marginBottom: 10, color: '#e3b341' }}>This statement changed since you confirmed it on {fmtDate(resp.acceptedAt)}. Read it and confirm again.</Help>}
        <TextBox>{cv.responsibilityText}</TextBox>
        {respOk ? (
          <Help>You confirmed this on {fmtDate(resp.acceptedAt)}.</Help>
        ) : (
          <>
            <Check><input type="checkbox" checked={respChecked} onChange={(e) => setRespChecked(e.target.checked)} /><span>I agree to this statement.</span></Check>
            <Row style={{ marginTop: 12 }}><PrimaryBtn onClick={confirmResponsibility} disabled={!respChecked || respSaving}>{respSaving ? 'Saving…' : 'Confirm'}</PrimaryBtn></Row>
          </>
        )}
      </Card>

      <Card style={required && lic?.status !== 'verified' ? { borderColor: lic?.status === 'rejected' ? 'rgba(248,81,73,0.5)' : 'rgba(210,153,34,0.5)' } : undefined}>
        <CardTitle>
          <span>Professional licence{required ? '' : ' (optional)'}</span>
          <Row>
            {lic ? <Pill $bg={lsBg} $fg={lsFg}>{lsText}</Pill> : required ? <Pill $bg="rgba(210,153,34,0.2)" $fg="#e3b341">Required</Pill> : null}
            {!required && <GhostBtn type="button" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => setOpen((o) => !o)}>{open ? 'Hide' : lic ? 'Show' : 'Add a licence'}</GhostBtn>}
          </Row>
        </CardTitle>
        {required
          ? <Help style={{ marginTop: 0, color: '#c9d1d9' }}>This field is regulated: a licensed professional must stand behind the agent.</Help>
          : <Help style={{ marginTop: 0 }}>A verified licence adds a “Licensed professional” badge to the agent{cv.lane && cv.lane !== 'paid' ? ' and lets it give advice on the visitor’s own case instead of general information' : ''}.</Help>}

        {lic?.status === 'rejected' && (
          <Help style={{ color: '#ff7b72' }}>iMaven could not verify this licence{lic.rejectedReason ? `: ${lic.rejectedReason}` : '.'} Fix the details or upload a clearer document and it will be reviewed again.</Help>
        )}
        {lic?.status === 'submitted' && <Help>{lic.hasDocument ? `Sent on ${fmtDate(lic.submittedAt)}. iMaven will verify it and email you.` : 'Upload the licence document so iMaven can verify it.'}</Help>}
        {lic?.status === 'verified' && <Help style={{ color: '#56d364' }}>Verified on {fmtDate(lic.verifiedAt)}.</Help>}

        {(required || open) && (
          <>
            <FormGrid>
              <div><Label>Name on the licence</Label><Input value={lf.holderName} maxLength={120} onChange={(e) => setL({ holderName: e.target.value })} /></div>
              <div><Label>Profession</Label><Input value={lf.profession} maxLength={120} placeholder="e.g. Regulated Canadian Immigration Consultant" onChange={(e) => setL({ profession: e.target.value })} /></div>
              <div><Label>Regulator</Label><Input value={lf.regulator} maxLength={160} placeholder="e.g. CICC" onChange={(e) => setL({ regulator: e.target.value })} /></div>
              <div><Label>Licence number</Label><Input value={lf.licenseNumber} maxLength={80} onChange={(e) => setL({ licenseNumber: e.target.value })} /></div>
            </FormGrid>
            <Label>Countries where it is valid</Label>
            <Row>
              {LICENCE_COUNTRIES.map(([code, name]) => <Chip key={code} type="button" $on={lf.jurisdictions.includes(code)} onClick={() => toggleCountry(code)}>{name}</Chip>)}
              {extraCodes.map((code) => <Chip key={code} type="button" $on onClick={() => toggleCountry(code)}>{code} ✕</Chip>)}
              <Input style={{ width: 90 }} value={isoInput} maxLength={2} placeholder="Other" onChange={(e) => setIsoInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addIso(); } }} />
              <GhostBtn type="button" style={{ padding: '8px 12px' }} onClick={addIso} disabled={!isoInput.trim()}>Add</GhostBtn>
            </Row>
            <Help>Other: a two-letter country code (ISO), e.g. GB or FR.</Help>
            <Label>Expiry date (optional)</Label>
            <Input type="date" style={{ maxWidth: 220 }} min={today} value={lf.expiresAt} onChange={(e) => setL({ expiresAt: e.target.value })} />
            <Label>Statement</Label>
            <TextBox>{cv.attestationText}</TextBox>
            <Check><input type="checkbox" checked={lf.attestationAccepted} onChange={(e) => setL({ attestationAccepted: e.target.checked })} /><span>I confirm this statement.{lic ? ' (Needed each time you save.)' : ''}</span></Check>
            {lic?.status === 'verified' && <Help style={{ color: '#e3b341' }}>Saving changes sends the licence back to iMaven for review{required ? ' and hides the agent until it is verified again' : ''}.</Help>}
            <Row style={{ marginTop: 14 }}><PrimaryBtn onClick={saveLicence} disabled={!canSave}>{licSaving ? 'Saving…' : lic ? 'Save changes' : 'Save licence'}</PrimaryBtn></Row>

            <Label style={{ marginTop: 20 }}>Licence document</Label>
            {!lic ? (
              <Help style={{ marginTop: 0 }}>Save the licence details first, then upload a copy of the licence.</Help>
            ) : (
              <>
                <Help style={{ marginTop: 0, marginBottom: 8 }}>{lic.hasDocument ? `Uploaded: ${lic.documentName || 'licence document'}` : 'No document yet. A copy of the licence is needed for verification.'} PDF, JPG or PNG, up to 10 MB. Only iMaven sees it.</Help>
                <Row>
                  <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" style={{ display: 'none' }} onChange={(e) => upload(e.target.files?.[0])} />
                  <PrimaryBtn type="button" onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? 'Uploading…' : lic.hasDocument ? 'Replace document' : 'Upload document'}</PrimaryBtn>
                  {lic.hasDocument && <GhostBtn type="button" onClick={viewDocument}>View document ↗</GhostBtn>}
                </Row>
              </>
            )}
          </>
        )}
      </Card>
    </>
  );
}
const EMPTY_FORM = { name: '', headline: '', description: '', persona: '', sampleQuestions: [], samples: [], priceTier: 0, imageUrl: '' };

export default function AgentPage() {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const [data, setData] = useState(null);              // { agents, pricing, owner, taxonomy }
  const [selectedId, setSelectedId] = useState(null);  // agent id or 'new'
  const [form, setForm] = useState(EMPTY_FORM);
  const [createForm, setCreateForm] = useState({ name: '', category: '', subcategories: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runs, setRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [starting, setStarting] = useState(false);   // between the click and the server's answer
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState('');
  const [earnings, setEarnings] = useState(null);
  const [qInput, setQInput] = useState('');
  const pollRef = useRef(null);

  const agents = data?.agents || [];
  const agent = agents.find((a) => a.id === selectedId) || null;
  const tiers = data?.pricing?.tiers || [1, 3, 5];
  const currency = data?.pricing?.currency || 'USD';
  const feePct = data?.pricing?.platformFeePct ?? 30;

  const load = useCallback(async (keepSelection = true) => {
    try {
      const r = await api.get('/agents/me');
      setData(r.data);
      const list = r.data?.agents || [];
      setSelectedId((cur) => (keepSelection && cur && (cur === 'new' || list.some((a) => a.id === cur))) ? cur : (list[0]?.id || 'new'));
      if (!createForm.category && r.data?.owner?.categoryLabel) setCreateForm((f) => ({ ...f, category: r.data.owner.categoryLabel, subcategories: r.data.owner.subcategories || [] }));
    } catch (e) {
      showToast('Could not load your agents', 'error');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, showToast]);

  useEffect(() => { load(); }, [load]);

  // When the selection changes: fill the form, load programs, runs and earnings for that agent.
  useEffect(() => {
    if (!agent) return;
    setForm({ name: agent.name || '', headline: agent.headline || '', description: agent.description || '', persona: agent.persona || '', sampleQuestions: agent.sampleQuestions || [], samples: agent.samples || [], priceTier: agent.priceTier ?? 0, imageUrl: agent.imageUrl || '' });
    setActiveRun(null);
    Promise.all([
      api.get(`/agents/${agent.id}/programs`).catch(() => ({ data: { programs: [] } })),
      api.get(`/agents/${agent.id}/bench/runs`).catch(() => ({ data: { runs: [] } })),
      api.get(`/agents/${agent.id}/earnings`).catch(() => ({ data: null })),
    ]).then(([p, r, e]) => {
      setPrograms(p.data?.programs || []);
      setProgramId(p.data?.programs?.[0]?.programId || '');
      setRuns(r.data?.runs || []);
      setEarnings(e.data || null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id]);

  // Poll an in-progress run
  useEffect(() => {
    if (!activeRun || activeRun.status !== 'running' || !agent) { if (pollRef.current) clearInterval(pollRef.current); return; }
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/agents/${agent.id}/bench/runs/${activeRun._id}`);
        setActiveRun(r.data.run);
        if (r.data.run.status !== 'running') { clearInterval(pollRef.current); load(); }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRun?._id, activeRun?.status]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const replaceAgent = (updated) => setData((d) => ({ ...d, agents: (d?.agents || []).map((a) => (a.id === updated.id ? updated : a)) }));

  const create = async () => {
    if (!createForm.name.trim() || !createForm.category) return;
    setSaving(true);
    try {
      const r = await api.post('/agents', { name: createForm.name.trim(), category: createForm.category, subcategories: createForm.subcategories });
      showToast('Agent created', 'success');
      setData((d) => ({ ...d, agents: [...(d?.agents || []), r.data.agent] }));
      setSelectedId(r.data.agent.id);
      setCreateForm((f) => ({ ...f, name: '' }));
    } catch (e) { showToast(e?.response?.data?.error || 'Could not create', 'error'); }
    finally { setSaving(false); }
  };

  const save = async () => {
    if (!agent) return;
    setSaving(true);
    try { const r = await api.put(`/agents/${agent.id}`, form); replaceAgent(r.data.agent); showToast('Saved', 'success'); }
    catch (e) { showToast(e?.response?.data?.error || 'Could not save', 'error'); }
    finally { setSaving(false); }
  };

  const act = async (path, okMsg) => {
    if (!agent) return;
    try { const r = await api.post(`/agents/${agent.id}/${path}`); if (r.data?.agent) replaceAgent(r.data.agent); showToast(okMsg, 'success'); }
    catch (e) { showToast(e?.response?.data?.error || 'Action failed', 'error'); }
  };

  const runTest = async () => {
    if (!agent || starting) return;
    setStarting(true);
    try {
      const res = await api.post(`/agents/${agent.id}/bench/run`, { programId: programId || undefined });
      const r = await api.get(`/agents/${agent.id}/bench/runs/${res.data.runId}`);
      setActiveRun(r.data.run);
      setRuns((list) => (list.some((x) => x._id === r.data.run._id) ? list : [r.data.run, ...list]));
      showToast(`Test started on "${res.data.programName}". This takes a few minutes; the transcript fills in live below.`, 'info');
    } catch (e) { showToast(e?.response?.data?.error || 'Could not start the test', 'error'); }
    finally { setStarting(false); }
  };

  const PHASE_LABEL = { preparing: 'generating the test visitor…', identification: 'identification', diagnostic: 'intake in progress', reviewing: 'reviewer is checking the conversation…', complete: 'complete', error: 'error' };

  // The reviewer is an AI opinion and can be wrong. The owner can accept a flagged run as "this is
  // what I want"; the agent becomes submittable and the note is shown to the iMaven reviewer.
  const acceptRun = async (withdraw) => {
    if (!agent || !activeRun) return;
    let note = '';
    if (!withdraw) {
      note = window.prompt('Why is this conversation acceptable for your intake? This note is shown to the iMaven reviewer.', '');
      if (note === null) return;
    }
    try {
      const url = `/agents/${agent.id}/bench/runs/${activeRun._id}/accept`;
      const r = withdraw ? await api.delete(url) : await api.post(url, { note });
      if (r.data?.agent) replaceAgent(r.data.agent);
      if (r.data?.run) setActiveRun(r.data.run);
      setRuns((list) => list.map((x) => (x._id === activeRun._id ? { ...x, ownerAcceptance: r.data?.run?.ownerAcceptance } : x)));
      showToast(withdraw ? 'Acceptance withdrawn' : 'Run accepted. You can submit the agent for review.', 'success');
    } catch (e) { showToast(e?.response?.data?.error || 'Action failed', 'error'); }
  };

  const openRun = async (id) => { if (!agent) return; try { const r = await api.get(`/agents/${agent.id}/bench/runs/${id}`); setActiveRun(r.data.run); } catch { /* ignore */ } };

  const remove = async () => {
    if (!agent || !window.confirm(`Delete "${agent.name}"? If it has delivered consultations it will be paused instead.`)) return;
    try { await api.delete(`/agents/${agent.id}`); showToast('Agent deleted', 'success'); setData((d) => ({ ...d, agents: (d?.agents || []).filter((a) => a.id !== agent.id) })); setSelectedId('new'); }
    catch (e) { showToast(e?.response?.data?.error || 'Could not delete', 'error'); load(); }
  };

  if (loading) return <Page><Subtitle>Loading…</Subtitle></Page>;

  const cat = (data?.taxonomy || []).find((t) => t.label === createForm.category || t.slug === createForm.category);
  const status = agent?.status || 'draft';
  const [stText, stBg, stFg] = STATUS_TEXT[status] || STATUS_TEXT.draft;
  const stepIndex = ['draft', 'testing', 'pending_review', 'published'].indexOf(status === 'paused' ? 'published' : status);

  return (
    <Page>
      <Title>My AI Agents</Title>
      <Subtitle>
        Publish AI advisors on imavenai.com. A visitor who chooses your agent gets an instant intake and a written consultation;
        you earn <strong>{100 - feePct}%</strong> of every paid consultation and iMaven covers the AI cost. One agent per area of advice;
        every agent is tested and reviewed by iMaven before it goes live.
      </Subtitle>

      <Grid>
        {/* ---- left: list + create ---- */}
        <div>
          {agents.map((a) => {
            const [t, bg, fg] = STATUS_TEXT[a.status] || STATUS_TEXT.draft;
            return (
              <AgentItem key={a.id} $active={a.id === selectedId} onClick={() => setSelectedId(a.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}><strong style={{ fontSize: 14 }}>{a.name}</strong><Pill $bg={bg} $fg={fg}>{t}</Pill></div>
                <div style={{ fontSize: 12, color: '#8b98a5', marginTop: 4 }}>{a.category}</div>
              </AgentItem>
            );
          })}
          <AgentItem $active={selectedId === 'new'} onClick={() => setSelectedId('new')}>+ New agent</AgentItem>
        </div>

        {/* ---- right: create or detail ---- */}
        <div>
          {selectedId === 'new' && (
            <Card>
              <CardTitle>Create an agent</CardTitle>
              <Label>Name</Label>
              <Input value={createForm.name} maxLength={80} placeholder="e.g. Ask R.K. — Canadian immigration AI" onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} />
              <Label>Area of advice</Label>
              <Select value={createForm.category} onChange={(e) => setCreateForm((f) => ({ ...f, category: e.target.value, subcategories: [] }))}>
                <option value="">Choose…</option>
                {(data?.taxonomy || []).map((t) => <option key={t.slug} value={t.label}>{t.label}</option>)}
              </Select>
              {cat && cat.subcategories?.length > 0 && (
                <>
                  <Label>{cat.subLabel}</Label>
                  <Row>
                    {cat.subcategories.map((s) => {
                      const on = createForm.subcategories.includes(s.slug);
                      return <Chip key={s.slug} $on={on} type="button" onClick={() => setCreateForm((f) => ({ ...f, subcategories: on ? f.subcategories.filter((x) => x !== s.slug) : (cat.subMulti ? [...f.subcategories, s.slug] : [s.slug]) }))}>{s.label}</Chip>;
                    })}
                  </Row>
                </>
              )}
              <Label>Brain</Label>
              <Help style={{ marginTop: 0 }}>This agent uses your own brain: everything you teach your brain in Synthora is used in its intake and consultations.</Help>
              <Row style={{ marginTop: 16 }}><PrimaryBtn onClick={create} disabled={saving || !createForm.name.trim() || !createForm.category}>{saving ? 'Creating…' : 'Create agent'}</PrimaryBtn></Row>
            </Card>
          )}

          {agent && (
            <>
              <Card>
                <CardTitle><span>{agent.name}</span><Pill $bg={stBg} $fg={stFg}>{stText}</Pill></CardTitle>
                <Steps>
                  {[['Create', 'Name, description, persona and price.'], ['Test', 'A simulated visitor talks to your agent; a reviewer checks the conversation.'], ['Review', 'iMaven approves the agent for the marketplace.'], ['Live', 'Visitors can choose your agent. Pause it any time.']].map(([t, d], i) => (
                    <Step key={t} $done={i < stepIndex} $active={i === stepIndex}><Dot $done={i < stepIndex} $active={i === stepIndex}>{i < stepIndex ? '✓' : i + 1}</Dot><span><strong>{t}</strong> — {d}</span></Step>
                  ))}
                </Steps>
                {status === 'rejected' && agent.rejectedReason && <Help style={{ color: '#ff7b72' }}>Reason: {agent.rejectedReason}</Help>}
                {agent.blocking?.length > 0 && status !== 'published' && status !== 'paused' && <Help>Before it can go live: {agent.blocking.map((b) => BLOCKING_TEXT[b] || b).join(' ')}</Help>}
                {(status === 'published' || status === 'paused') && agent.credential?.required && agent.blocking?.some((b) => b.startsWith('licence-')) && (
                  <Help style={{ color: '#e3b341' }}>Hidden from the marketplace: {agent.blocking.filter((b) => b.startsWith('licence-')).map((b) => BLOCKING_TEXT[b] || b).join(' ')}</Help>
                )}
                <Row style={{ marginTop: 14 }}>
                  {(status === 'draft' || status === 'testing' || status === 'rejected') && <PrimaryBtn onClick={() => act('submit', 'Submitted for review')} disabled={!agent.benchValid || !agent.headline || !agent.description || (agent.credential && !agent.credential.complete)}>Submit for review</PrimaryBtn>}
                  {status === 'published' && <GhostBtn onClick={() => act('pause', 'Agent paused')}>Pause</GhostBtn>}
                  {status === 'paused' && <PrimaryBtn onClick={() => act('resume', 'Agent is live again')}>Resume</PrimaryBtn>}
                  {agent.hostedChatUrl && <GhostBtn as="a" href={agent.hostedChatUrl} target="_blank" rel="noreferrer">Try it ↗</GhostBtn>}
                  <DangerBtn onClick={remove}>Delete</DangerBtn>
                </Row>
                <Help>This agent uses your own brain: everything you teach your brain in Synthora is used in its intake and consultations.</Help>
              </Card>

              {agent.credential && <AgentCredentialCards agent={agent} api={api} showToast={showToast} replaceAgent={replaceAgent} />}

              <Card>
                <CardTitle>Profile</CardTitle>
                <Label>Name</Label>
                <Input value={form.name} maxLength={80} onChange={(e) => set({ name: e.target.value })} />
                <Label>Headline</Label>
                <Input value={form.headline} maxLength={140} placeholder="One line visitors see on the card" onChange={(e) => set({ headline: e.target.value })} />
                <Label>Description</Label>
                <TextArea value={form.description} maxLength={4000} placeholder="What it helps with, what it knows, who it is for." onChange={(e) => set({ description: e.target.value })} />
                <Label>Persona and scope (instructions to the AI)</Label>
                <TextArea value={form.persona} maxLength={6000} placeholder="Tone, what to focus on, what to avoid, how to handle cases outside your scope…" onChange={(e) => set({ persona: e.target.value })} />
                <Help>Changing the persona or name after publication sends the agent back to testing and review.</Help>
                <Label>Photo / logo URL</Label>
                <Input value={form.imageUrl} placeholder="https://…" onChange={(e) => set({ imageUrl: e.target.value })} />
                <Label>Price of a written consultation</Label>
                <Row>
                  <Select value={form.priceTier} onChange={(e) => set({ priceTier: Number(e.target.value) })}>{tiers.map((t, i) => <option key={i} value={i}>{t} {currency}</option>)}</Select>
                  <Help style={{ marginTop: 0 }}>You receive {100 - feePct}% ({((tiers[form.priceTier] || 0) * (100 - feePct) / 100).toFixed(2)} {currency}) per paid consultation.</Help>
                </Row>
                <Label>Sample questions (shown on the agent's profile)</Label>
                <Input value={qInput} maxLength={300} placeholder="Type a question and press Enter" onChange={(e) => setQInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (qInput.trim() && form.sampleQuestions.length < 8) { set({ sampleQuestions: [...form.sampleQuestions, qInput.trim()] }); setQInput(''); } } }} />
                <Row style={{ marginTop: 8 }}>{form.sampleQuestions.map((q) => <Pill key={q} style={{ cursor: 'pointer' }} onClick={() => set({ sampleQuestions: form.sampleQuestions.filter((x) => x !== q) })}>{q} ✕</Pill>)}</Row>
                <Label>Sample results (up to 5)</Label>
                {form.samples.map((s, i) => (
                  <SampleBox key={i}>
                    <Input value={s.question} maxLength={500} placeholder="Question" onChange={(e) => set({ samples: form.samples.map((x, j) => (j === i ? { ...x, question: e.target.value } : x)) })} />
                    <TextArea style={{ marginTop: 8 }} value={s.answer} maxLength={6000} placeholder="What the consultation would say (short excerpt)" onChange={(e) => set({ samples: form.samples.map((x, j) => (j === i ? { ...x, answer: e.target.value } : x)) })} />
                    <GhostBtn style={{ marginTop: 8 }} onClick={() => set({ samples: form.samples.filter((_, j) => j !== i) })}>Remove</GhostBtn>
                  </SampleBox>
                ))}
                {form.samples.length < 5 && <GhostBtn style={{ marginTop: 10 }} onClick={() => set({ samples: [...form.samples, { question: '', answer: '' }] })}>+ Add a sample</GhostBtn>}
                <Row style={{ marginTop: 18 }}><PrimaryBtn onClick={save} disabled={saving || !form.name.trim()}>{saving ? 'Saving…' : 'Save'}</PrimaryBtn></Row>
              </Card>

              <Card>
                <CardTitle><span>Test Bench</span><Pill $bg={agent.benchValid ? 'rgba(63,185,80,0.2)' : 'rgba(210,153,34,0.2)'} $fg={agent.benchValid ? '#56d364' : '#e3b341'}>{agent.benchValidBy === 'owner' ? 'Accepted by you' : agent.benchValid ? 'Passed' : agent.testBench?.verdict || 'Not run yet'}</Pill></CardTitle>
                <Help>A simulated visitor (an AI persona for one of the agent's programs) talks to its real widget; a reviewer then checks the conversation. A passing run is required to submit and stays valid for 30 days. The reviewer can be wrong: if a flagged conversation is what you want, accept the run yourself and say why — the iMaven reviewer sees your note.</Help>
                <Row style={{ marginTop: 12 }}>
                  <Select value={programId} onChange={(e) => setProgramId(e.target.value)}>
                    {programs.length === 0 && <option value="">No programs found</option>}
                    {programs.map((p) => <option key={p.programId} value={p.programId}>{p.programName}</option>)}
                  </Select>
                  <PrimaryBtn onClick={runTest} disabled={starting || activeRun?.status === 'running' || programs.length === 0}>{starting ? 'Starting…' : activeRun?.status === 'running' ? 'Running…' : 'Run test'}</PrimaryBtn>
                </Row>
                {activeRun && (
                  <div style={{ marginTop: 14 }}>
                    <Row style={{ justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 13 }}><strong>{activeRun.programName}</strong> · {PHASE_LABEL[activeRun.phase] || activeRun.phase} · {Math.round((activeRun.totalMs || 0) / 1000)}s</span>
                      {activeRun.review?.verdict && <Pill $bg={activeRun.review.verdict === 'ok' ? 'rgba(63,185,80,0.2)' : 'rgba(210,153,34,0.2)'} $fg={activeRun.review.verdict === 'ok' ? '#56d364' : '#e3b341'}>{activeRun.review.verdict === 'ok' ? 'OK' : 'Needs attention'}</Pill>}
                    </Row>
                    {activeRun.status === 'done' && activeRun.review?.verdict === 'needs-attention' && (
                      <Row style={{ marginTop: 8, gap: 10, alignItems: 'center' }}>
                        {activeRun.ownerAcceptance?.acceptedAt
                          ? <>
                              <span style={{ fontSize: 12, color: '#56d364' }}>Accepted by you on {new Date(activeRun.ownerAcceptance.acceptedAt).toLocaleString()}{activeRun.ownerAcceptance.note ? ` — "${activeRun.ownerAcceptance.note}"` : ''}</span>
                              <GhostBtn onClick={() => acceptRun(true)}>Withdraw</GhostBtn>
                            </>
                          : <>
                              <span style={{ fontSize: 12, color: '#8b98a5' }}>Disagree with the reviewer? If this conversation is what you want from your intake:</span>
                              <GhostBtn onClick={() => acceptRun(false)}>Accept this run anyway</GhostBtn>
                            </>}
                      </Row>
                    )}
                    {activeRun.review?.summary && <Help style={{ color: '#c9d1d9' }}>{activeRun.review.summary}</Help>}
                    {activeRun.review?.findings?.length > 0 && <ReviewFindings review={activeRun.review} agentPagePath="/agent" />}
                    {activeRun.error && <Help style={{ color: '#ff7b72' }}>{activeRun.error}</Help>}
                    <Transcript style={{ marginTop: 10 }}>
                      {(activeRun.turns || []).map((t, i) => <Turn key={i}><div><b>Agent:</b> <span dangerouslySetInnerHTML={{ __html: t.assistant || '' }} /></div>{t.user && <div><b>Visitor:</b> {t.user}</div>}</Turn>)}
                      {(!activeRun.turns || activeRun.turns.length === 0) && <span style={{ color: '#8b98a5' }}>{activeRun.phase === 'preparing' ? 'Writing a realistic visitor for this program (about 20 seconds)…' : 'Waiting for the first turn…'}</span>}
                    </Transcript>
                  </div>
                )}
                {runs.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <Label>Previous runs</Label>
                    {runs.map((r) => (
                      <RunRow key={r._id} onClick={() => openRun(r._id)}>
                        <span>{new Date(r.createdAt).toLocaleString()} · {r.programName}</span>
                        <span style={{ color: r.status === 'error' ? '#ff7b72' : r.review?.verdict === 'ok' ? '#56d364' : '#e3b341' }}>{r.status === 'running' ? 'running' : r.status === 'error' ? 'error' : r.ownerAcceptance?.acceptedAt ? 'accepted by you' : r.review?.verdict || 'done'}</span>
                      </RunRow>
                    ))}
                  </div>
                )}
              </Card>

              <Card>
                <CardTitle>Earnings — {agent.name}</CardTitle>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                  <Stat><StatV>{earnings?.totals?.consultations ?? 0}</StatV><StatL>consultations</StatL></Stat>
                  <Stat><StatV>{earnings?.totals?.paid ?? 0}</StatV><StatL>paid</StatL></Stat>
                  <Stat><StatV>{(earnings?.totals?.royaltyAvailableUsd ?? 0).toFixed(2)}</StatV><StatL>{currency} available</StatL></Stat>
                  <Stat><StatV>{(earnings?.totals?.royaltyPaidUsd ?? 0).toFixed(2)}</StatV><StatL>{currency} paid out</StatL></Stat>
                </div>
                <Help>Royalties become available when the consultation is delivered and are paid out by iMaven.</Help>
              </Card>
            </>
          )}
        </div>
      </Grid>
    </Page>
  );
}
